import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import moment from 'moment-timezone';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Frequency, RRule, Weekday } from 'rrule';

import { getRRuleByWeekday, OrgData, rruleHasDtstart, validateAndGetRRuleFromStr } from '@pwp-common';

import {
  DATETIME_LOCAL_CONTROL_STR_FORMAT,
  getFieldControlValueOrDefault,
  getTimestampFromDateTimeLocalFieldControl,
} from '../../../common/objects/form-helper';
import { DatetimeValidator } from '../../../common/validators/datetime-validator/datetime-validator';
import { OrgDataService } from '../../../services/orgs/org-data/org-data.service';
import { FormGroupControlValueAccessor } from '../../generic/abstract-classes/form-group-control-value-accessor';
import { ProgressSpinnerModule } from '../progress-spinner/progress-spinner.module';

import { RRulePreset } from './enums';

const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RruleEditorComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => RruleEditorComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'app-rrule-editor',
  standalone: true,
  imports: [
    CommonModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    SelectButtonModule,
    TranslocoModule,
  ],
  templateUrl: './rrule-editor.component.html',
  styleUrls: ['./rrule-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class RruleEditorComponent extends FormGroupControlValueAccessor<any, string> implements OnInit {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Constants
  /////////////////////////////////////////////////////////////////////////////////////////////

  private static readonly PRESET_FREQUENCY_MAP: Record<
    Exclude<RRulePreset, RRulePreset.Custom>,
    { freq: Frequency; interval: number }
  > = {
    [RRulePreset.EveryDay]: { freq: Frequency.DAILY, interval: 1 },
    [RRulePreset.EveryWeek]: { freq: Frequency.WEEKLY, interval: 1 },
    [RRulePreset.EveryMonth]: { freq: Frequency.MONTHLY, interval: 1 },
    [RRulePreset.EveryYear]: { freq: Frequency.YEARLY, interval: 1 },
  };

  weekly = Frequency.WEEKLY;

  possiblePresets: { label: string; value: RRulePreset }[] = [
    { label: 'presetEveryDay', value: RRulePreset.EveryDay },
    { label: 'presetEveryWeek', value: RRulePreset.EveryWeek },
    { label: 'presetEveryMonth', value: RRulePreset.EveryMonth },
    { label: 'presetEveryYear', value: RRulePreset.EveryYear },
    { label: 'presetCustom', value: RRulePreset.Custom },
  ];

  possibleFrequencies: { label: string; value: Frequency }[] = [
    { label: 'daily', value: Frequency.DAILY },
    { label: 'weekly', value: Frequency.WEEKLY },
    { label: 'monthly', value: Frequency.MONTHLY },
    { label: 'yearly', value: Frequency.YEARLY },
  ];

  possibleValuesByWeekday: { label: string; value: Weekday }[] = [
    { label: 'byWeekdayMonday', value: RRule.MO },
    { label: 'byWeekdayTuesday', value: RRule.TU },
    { label: 'byWeekdayWednesday', value: RRule.WE },
    { label: 'byWeekdayThursday', value: RRule.TH },
    { label: 'byWeekdayFriday', value: RRule.FR },
    { label: 'byWeekdaySaturday', value: RRule.SA },
    { label: 'byWeekdaySunday', value: RRule.SU },
  ];

  possibleValuesWeekStart: { label: string; value: Weekday }[] = [
    { label: 'weekStartMonday', value: RRule.MO },
    { label: 'weekStartTuesday', value: RRule.TU },
    { label: 'weekStartWednesday', value: RRule.WE },
    { label: 'weekStartThursday', value: RRule.TH },
    { label: 'weekStartFriday', value: RRule.FR },
    { label: 'weekStartSaturday', value: RRule.SA },
    { label: 'weekStartSunday', value: RRule.SU },
  ];

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  orgData!: OrgData;

  rruleDidSpecifyTzId = false;

  rruleTimezone!: string;

  initializedWithRRuleStr = '';

  @Input() public disableStartEditing = false;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private orgDataService: OrgDataService,
  ) {
    super(changeDetectorRef);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.orgDataService
      .getOrgData()
      .pipe(untilDestroyed(this))
      .subscribe((orgData) => {
        this.orgData = orgData;
        this.setRRuleTimezone();
        this.form.patchValue(this.form.value);
        this.changeDetectorRef.detectChanges();
      });

    this.preset.valueChanges.pipe(untilDestroyed(this)).subscribe((value: RRulePreset) => {
      this.handlePresetChange(value);
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////

  defineForm() {
    const formConfig = {} as any;
    formConfig.preset = new UntypedFormControl(RRulePreset.Custom, [Validators.required]);
    formConfig.byWeekday = new UntypedFormControl(null, []);
    formConfig.frequency = new UntypedFormControl(Frequency.WEEKLY, [Validators.required]);
    formConfig.weekStart = new UntypedFormControl(null, []);
    formConfig.interval = new UntypedFormControl(1, [Validators.required, Validators.min(1)]);

    const initializedWIthTime = moment().subtract(1, 'hour').format(DATETIME_LOCAL_CONTROL_STR_FORMAT);
    formConfig.dtStart = new UntypedFormControl(initializedWIthTime, [
      Validators.required,
      DatetimeValidator.isBefore(moment(), undefined),
      DatetimeValidator.isValid(),
    ]);
    this.form = new UntypedFormGroup(formConfig);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////

  writeValue(rruleStr: string) {
    this.initializedWithRRuleStr = rruleStr;
    const rrule = validateAndGetRRuleFromStr(rruleStr ?? '', false);
    if (!rruleHasDtstart(rruleStr, false)) {
      this.rruleDidSpecifyTzId = false;
      /**
       * Emit if the RRule is initialized with an invalid str. Alternatively,
       * we could do nothing and leave the form as invalid
       * (which is what our custom form controls do by default)
       */
      if (isNil(rrule) || isNil(rruleStr) || rruleStr === '') {
        this.form.patchValue(
          {
            preset: RRulePreset.Custom,
            dtStart: moment().subtract(1, 'hour').format(DATETIME_LOCAL_CONTROL_STR_FORMAT),
            interval: 1,
            frequency: Frequency.WEEKLY,
          },
          { emitEvent: true },
        );
        this.changeDetectorRef.detectChanges();
        return;
      }

      const frequency = rrule.options.freq ?? Frequency.DAILY;
      const { interval } = rrule.options;
      const byWeekday = (getRRuleByWeekday(rruleStr, true) ?? []).map((day) => new Weekday(day));
      const weekStart = new Weekday(rrule.options.wkst);
      const preset = RruleEditorComponent.getPresetFromRRule(rrule);
      this.form.patchValue(
        {
          preset,
          dtStart: moment().subtract(1, 'hour').format(DATETIME_LOCAL_CONTROL_STR_FORMAT),
          interval,
          frequency,
          byWeekday,
          weekStart,
        },
        { emitEvent: true },
      );
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.setRRuleTimezone();
    const dtStart = rrule!.options.dtstart.toISOString().split(':').slice(0, 2).join(':');
    const byWeekday = isNil(rruleStr) ? [] : (getRRuleByWeekday(rruleStr, true) ?? []).map((day) => new Weekday(day));
    const weekStart = new Weekday(rrule!.options.wkst);
    const frequency = rrule!.options.freq ?? Frequency.DAILY;
    const { interval } = rrule!.options;
    const preset = RruleEditorComponent.getPresetFromRRule(rrule!);
    super.writeValue({
      frequency,
      interval,
      preset,
      dtStart,
      byWeekday,
      weekStart,
    });
    this.onChange?.(rruleStr);
  }

  private static getPresetFromRRule(rrule: RRule): RRulePreset {
    const { freq, interval, byweekday } = rrule.options;

    if (interval !== 1 || (byweekday && byweekday.length > 0)) {
      return RRulePreset.Custom;
    }

    switch (freq) {
      case Frequency.DAILY:
        return RRulePreset.EveryDay;
      case Frequency.WEEKLY:
        return RRulePreset.EveryWeek;
      case Frequency.MONTHLY:
        return RRulePreset.EveryMonth;
      case Frequency.YEARLY:
        return RRulePreset.EveryYear;
      default:
        return RRulePreset.Custom;
    }
  }

  private handlePresetChange(preset: RRulePreset): void {
    if (preset === RRulePreset.Custom) {
      return;
    }
    const config = RruleEditorComponent.PRESET_FREQUENCY_MAP[preset];
    this.frequency.setValue(config.freq, { emitEvent: false });
    this.interval.setValue(config.interval, { emitEvent: false });
    this.byWeekday.setValue(null, { emitEvent: false });
    // weekStart is intentionally preserved.
  }

  private setRRuleTimezone() {
    const parsedRRule = validateAndGetRRuleFromStr(this.initializedWithRRuleStr, false);
    if (!rruleHasDtstart(this.initializedWithRRuleStr, false)) {
      this.rruleTimezone = this.orgData?.getTimezone();
      return;
    }
    this.rruleTimezone = parsedRRule.options.tzid;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////

  parseValueChange(rruleFormValue: any): string {
    const rruleInputTime = getTimestampFromDateTimeLocalFieldControl(this.dtStart, 'UTC', moment.unix(0));
    const currentPreset: RRulePreset = rruleFormValue.preset ?? RRulePreset.Custom;
    const { freq, interval, wkst, byweekday } = this.buildRRuleParams(currentPreset);

    return new RRule({
      interval,
      freq,
      dtstart: rruleInputTime?.toDate(),
      tzid: this.rruleTimezone,
      byweekday,
      wkst,
    }).toString();
  }

  private buildRRuleParams(preset: RRulePreset): {
    freq: Frequency | undefined;
    interval: number | undefined;
    wkst: Weekday | undefined;
    byweekday: Weekday[] | undefined;
  } {
    const wkst = getFieldControlValueOrDefault(this.weekStart, undefined);

    if (preset === RRulePreset.Custom) {
      const freq = getFieldControlValueOrDefault(this.frequency, undefined);
      return {
        freq,
        interval: getFieldControlValueOrDefault(this.interval, undefined),
        wkst,
        byweekday: freq === Frequency.WEEKLY ? getFieldControlValueOrDefault(this.byWeekday, undefined) : undefined,
      };
    }

    return { ...RruleEditorComponent.PRESET_FREQUENCY_MAP[preset], wkst, byweekday: undefined };
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Controls
  /////////////////////////////////////////////////////////////////////////////////////////////

  get preset() {
    return this.form.get('preset') as UntypedFormControl;
  }

  get dtStart() {
    return this.form.get('dtStart') as UntypedFormControl;
  }

  get byWeekday() {
    return this.form.get('byWeekday') as UntypedFormControl;
  }

  get frequency() {
    return this.form.get('frequency') as UntypedFormControl;
  }

  get weekStart() {
    return this.form.get('weekStart') as UntypedFormControl;
  }

  get interval() {
    return this.form.get('interval') as UntypedFormControl;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////

  public makeValidationErrors(): ValidationErrors {
    return {
      'rrule-editor': this.form.value,
    };
  }
}
